
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Loader2, Save } from 'lucide-react';
import { saveBusinessSetting } from '@/utils/businessService';

const contactFormSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" })
});

interface ContactSettingsFormProps {
  initialEmail: string;
  onSaved?: () => void;
}

const ContactSettingsForm: React.FC<ContactSettingsFormProps> = ({ initialEmail, onSaved }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<z.infer<typeof contactFormSchema>>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      email: initialEmail
    }
  });
  
  const onSubmit = async (data: z.infer<typeof contactFormSchema>) => {
    setIsSubmitting(true);
    
    try {
      await saveBusinessSetting('contactEmail', data.email);
      
      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      console.error('Error saving contact email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Contact Form Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Contact Email</FormLabel>
                  <FormControl>
                    <Input placeholder="contact@yourcompany.com" {...field} />
                  </FormControl>
                  <FormDescription>
                    This email address will receive all contact form submissions from your users.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="mt-4"
              variant="oval"
              size="oval"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" /> Save Settings
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ContactSettingsForm;
